import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, AlertTriangle, FileSignature } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "@/hooks/use-toast";

export function LegalConsentModal() {
  const { user, session } = useAuth();
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;

    // 1. Tenta checar no user_metadata do Supabase (persistente por conta)
    const serverConsent = user?.user_metadata?.legal_consent_accepted;
    
    // 2. Fallback pro localStorage (persistente por navegador)
    const localConsent = localStorage.getItem(`fiscal_legal_consent_${user?.id}`);

    if (!serverConsent && !localConsent) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [session, user?.id, user?.user_metadata?.legal_consent_accepted]);

  const handleAccept = async () => {
    if (!accepted || !user) return;
    
    setLoading(true);
    try {
      // Salva no Supabase (Nuvem)
      const { error } = await supabase.auth.updateUser({
        data: { 
          legal_consent_accepted: true, 
          legal_consent_at: new Date().toISOString() 
        }
      });

      if (error) throw error;

      // Salva no LocalStorage (Cache rápido)
      localStorage.setItem(`fiscal_legal_consent_${user.id}`, "true");
      
      setOpen(false);
      toast({
        title: "Consentimento registrado",
        description: "Seu aceite aos termos foi salvo com sucesso.",
      });
    } catch (err: any) {
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível registrar seu consentimento. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val) return; 
      setOpen(val);
    }}>
      <DialogContent className="max-w-xl p-0 overflow-hidden border-blue-900/20 dark:border-blue-500/20 shadow-2xl [&>button]:hidden">
        <div className="bg-gradient-to-r from-red-500/10 to-amber-500/10 border-b border-red-500/20 p-6 flex items-start gap-4">
          <div className="size-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
            <ShieldAlert className="size-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <DialogTitle className="text-xl font-bold text-red-700 dark:text-red-400">Aviso Legal Obrigatório</DialogTitle>
            <DialogDescription className="mt-1 text-red-900/70 dark:text-red-200/70 text-sm font-medium">
              Sua conta exige o aceite dos termos para operar.
            </DialogDescription>
          </div>
        </div>

        <div className="p-6 space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          <p>
            O <strong>Extrator Fiscal</strong> é uma ferramenta analítica auxiliar. Em conformidade com a legislação brasileira e regulamentações do Conselho Federal de Contabilidade (CFC), informamos que:
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <AlertTriangle className="size-5 shrink-0 text-amber-500 mt-0.5" />
              <span>Esta plataforma <strong>não substitui</strong> o parecer de um <strong>Profissional Contábil habilitado</strong>.</span>
            </li>
            <li className="flex gap-3">
              <FileSignature className="size-5 shrink-0 text-amber-500 mt-0.5" />
              <span>Qualquer decisão fiscal baseada nestes dados exige a <strong>validação prévia do seu contador</strong>.</span>
            </li>
          </ul>
          
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800">
            <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <Checkbox 
                checked={accepted} 
                onCheckedChange={(c) => setAccepted(c === true)} 
                className="mt-0.5"
              />
              <span className="text-sm font-semibold leading-snug">
                Declaro estar ciente de que a plataforma fornece apenas indicativos matemáticos e que consultarei um contador responsável antes de qualquer tomada de decisão fiscal.
              </span>
            </label>
          </div>
        </div>

        <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-950/50 border-t border-slate-200 dark:border-slate-800">
          <Button 
            onClick={handleAccept} 
            disabled={!accepted || loading}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold"
          >
            {loading ? "Salvando..." : "Compreendo e Aceito os Termos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
